import {useCallback, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Flex, Image, Skeleton, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {CoverVariantTile} from '@amblique/king-living/app/components/complex-customize/option-step';
import {PIM_SQUARE_THUMBNAIL_PX} from '@amblique/king-living/app/components/configuration-selector/utils';
import {formatCoverItemTypeLabel} from '@amblique/king-living/app/hooks/use-customize-steps';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {resolveColorSwatchUrl, resolveMaterialThumbnailUrl} from '@amblique/king-living/app/utils/utils';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';

/**
 * Step 1: Choose your cover.
 * Tabs by itemType (Fabric, Leather, etc.), then "Shop by colour" | "Shop by collection".
 * Hide "Shop by collection" tab when byCollection is empty for that itemType.
 *
 * Extra price (two levels):
 * - #1 Group: Per itemType tab, extra = cheapest variant in that group - masterMinPrice (shown below tab label).
 * - #2 Variant: Per variant tile, extra = variant.salePrice - masterMinPrice (shown below variant name).
 *
 * @param {Array} coverGroups - Cover groups data
 * @param {string} [currency] - Currency code (e.g. 'USD', 'AUD')
 * @param {number} [masterMinPrice] - Master product min price for extra price calculation
 * @param {Object} selection - Current selection
 * @param {Function} onSelect - Selection handler
 * @param {boolean} [isMobile] - Whether on mobile (shows "View detail" link on tiles)
 * @param {Function} [onViewDetails] - View details handler (optional, mobile only)
 */
export function CoverStep({
  coverGroups,
  currency,
  isSimplePDPCoverFlyout = false,
  masterMinPrice,
  selection,
  onSelect,
  isMobile,
  onViewDetails,
}) {
  const intl = useIntl();
  const currencyCode = currency ?? 'USD';
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const [activeItemTypeIndex, setActiveItemTypeIndex] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState('color');

  // #1 Group extra price: per itemType group, cheapest variant in that group - masterMinPrice
  const itemTypeExtraPrices = useMemo(() => {
    if (masterMinPrice == null || masterMinPrice <= 0) return {};

    const priceMap = {};

    coverGroups.forEach((group) => {
      const allVariantsInGroup = [
        ...group.byColor.flatMap((c) => c.variants),
        ...group.byCollection.flatMap((c) => c.variants)
      ];

      const validPrices = allVariantsInGroup
        .map((v) => v.salePrice)
        .filter((p) => p != null && p > 0);

      if (validPrices.length > 0) {
        const cheapest = Math.min(...validPrices);
        const extraPrice = cheapest - masterMinPrice;

        // Include 0 so tab shows "From + $0.00" when extra is zero
        if (extraPrice >= 0) {
          priceMap[group.itemType] = extraPrice;
        }
      }
    });

    return priceMap;
  }, [coverGroups, masterMinPrice]);

  const activeGroup = coverGroups[activeItemTypeIndex];

  if (!activeGroup) {
    return null;
  }

  const hasCollections = activeGroup.byCollection.length > 0;
  const showColorTab = activeGroup.byColor.length > 0;

  const colorGroups = activeGroup.byColor;
  const collectionGroups = activeGroup.byCollection;

  const displayGroups = activeSubTab === 'color' ? colorGroups : collectionGroups;
  const groupKey = activeSubTab === 'color' ? 'color' : 'collectionName';

  const handleVariantSelect = (variant) => {
    onSelect('cover', {
      productId: variant.productId,
      variationValues: variant.variationValues,
      name: variant.swatchName || variant.name,
      salePrice: variant.salePrice ?? null,
      price: variant.price ?? null,
      imageUrl: variant.swatchImageUrl ?? null,
      selectedAt: Date.now(),
    });
  };

  const handleSubTabChange = (newTab) => {
    if (activeSubTab !== newTab) {
      onSelect('cover', null);
      setActiveSubTab(newTab);
    }
  };

  const handleCoverSeeDetails = useCallback(
    (variant) => {
      onViewDetails?.(variant, { coverItemType: activeGroup?.itemType });
    },
    [onViewDetails, activeGroup?.itemType]
  );

  const selectedCoverProductId = selection?.cover?.productId;

  return (
    <Box className="cover-step-container" {...styles.coverStep}>
      <Text {...styles.coverStepTitle}>
        <FormattedMessage
          defaultMessage="Choose your finish"
          id="customize_panel.step.cover.finish.title"
        />
      </Text>

      {/* ItemType tabs (Fabric, Leather, etc.) */}
      <Flex className="cover-step__tabs" {...styles.itemTypeTabBar}>
        {coverGroups.map((group, idx) => {
          const extraPrice = itemTypeExtraPrices[group.itemType];
          const materialThumbSrc = resolveMaterialThumbnailUrl(group.itemType);

          return (
            <Box
              key={group.itemType}
              className="cover-step__tabs__item"
              {...styles.itemTypeTab}
              {...(idx === activeItemTypeIndex && styles.itemTypeTabActive)}
              as="button"
              type="button"
              onClick={() => setActiveItemTypeIndex(idx)}
            >
              <Box className="cover-step__tabs__item-thumbnail" {...styles.itemTypeTabThumbnail}>
                {materialThumbSrc ? (
                  <Image
                    alt=""
                    height="100%"
                    htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                    htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                    objectFit="cover"
                    src={materialThumbSrc}
                    width="100%"
                  />
                ) : (
                  <Skeleton borderRadius="4px" height="100%" width="100%" />
                )}
              </Box>
              <Flex className="cover-step__tabs__item-text" {...styles.itemTypeTabTextColumn}>
                <Text className="cover-step__tabs__item-name" {...styles.itemTypeTabName}>
                  {formatCoverItemTypeLabel(intl, group.itemType)}
                </Text>
                {extraPrice !== undefined && (
                  <Text
                    className="cover-step__tabs__item-extra-price"
                    title={intl.formatMessage(
                      {
                        defaultMessage: 'From {fromPlusPrice}',
                        id: 'customize_panel.step.cover.tab.from_extra_price',
                      },
                      {
                        fromPlusPrice: `+ ${formatCurrencyHideTrailingZeroCents(intl, extraPrice, currencyCode)}`,
                      }
                    )}
                    {...styles.itemTypeTabPrice}
                  >
                    <FormattedMessage
                      defaultMessage="From {fromPlusPrice}"
                      id="customize_panel.step.cover.tab.from_extra_price"
                      values={{
                        fromPlusPrice: (
                          <Box as="span" display="inline">
                            +
                            {'\u00A0'}
                            {formatCurrencyHideTrailingZeroCents(intl, extraPrice, currencyCode)}
                          </Box>
                        ),
                      }}
                    />
                  </Text>
                )}
              </Flex>
            </Box>
          );
        })}
      </Flex>

      {/* Sub-tabs: Shop by colour | Shop by collection */}
      {showColorTab && (
        <>
          <Text {...styles.coverStepTitle}>
            <FormattedMessage
              defaultMessage="Choose your material"
              id="customize_panel.step.cover.material.title"
            />
          </Text>
          <Flex className="cover-step__sub-tabs" {...styles.coverTabBar}>
            <Box
              className="cover-step__sub-tabs__item"
              {...styles.coverTab}
              {...(activeSubTab === 'color' && styles.coverTabActive)}
              as="button"
              type="button"
              onClick={() => handleSubTabChange('color')}
            >
              <FormattedMessage
                defaultMessage="Shop by colour"
                id="customize_panel.step.cover.tab.shop_by_color"
              />
            </Box>
            {hasCollections && (
              <Box
                className="cover-step__sub-tabs__item"
                {...styles.coverTab}
                {...(activeSubTab === 'collection' && styles.coverTabActive)}
                as="button"
                type="button"
                onClick={() => handleSubTabChange('collection')}
              >
                <FormattedMessage
                  defaultMessage="Shop by collection"
                  id="customize_panel.step.cover.tab.shop_by_collections"
                />
              </Box>
            )}
          </Flex>
        </>
      )}

      {/* Color/Collection groups and variant swiper */}
      <Box className="cover-content">
        {displayGroups.map((group, groupIdx) => {
          const colorSwatchSrc = activeSubTab === 'color' ? resolveColorSwatchUrl(group.color) : null;
          const groupLabelId = `cover-group-label-${activeSubTab}-${groupIdx}`;

          return (
            <Box
              key={group[groupKey]}
              aria-labelledby={groupLabelId}
              className="cover-group"
              marginBottom={6}
              role={isSimplePDPCoverFlyout ? 'group' : 'radiogroup'}
              {...styles.coverGroup}
            >
              <Box className="cover-group__label-container" {...styles.coverGroupLabelContainer}>
                {activeSubTab === 'color' ? (
                  <Box className="cover-group__label_icon" {...styles.coverGroupLabelIcon}>
                    {colorSwatchSrc ? (
                      <Image
                        alt={group[groupKey]}
                        borderRadius="sm"
                        height="100%"
                        htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                        htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                        objectFit="cover"
                        src={colorSwatchSrc}
                        width="100%"
                      />
                    ) : (
                      <Skeleton borderRadius="sm" height="100%" width="100%" />
                    )}
                  </Box>
                ) : null}
                <Box
                  as="span"
                  className="cover-group__label"
                  id={groupLabelId}
                  {...styles.coverGroupLabel}
                >
                  {group[groupKey]}
                </Box>
                <Box as="span" className="cover-group__label-total" {...styles.coverGroupLabelTotal}>
                  <FormattedMessage
                    defaultMessage="{total, plural, one {# option} other {# options}}"
                    id="customize_panel.step.cover.group.total_variants"
                    values={{
                      total: group.variants.length,
                    }}
                  />
                </Box>
              </Box>
              <Swiper className="cover-swiper" presets="cover-swiper" variant="cover-swiper">
                {group.variants.map((v) => {
                  const isSelected = selectedCoverProductId === v.productId;

                  return (
                    <Box key={v.productId} className="cover-group-item-container" flexShrink={0}>
                      <CoverVariantTile
                        currency={currency}
                        isMobile={isMobile}
                        isSelected={isSelected}
                        masterMinPrice={masterMinPrice}
                        styles={styles}
                        tabIndex={0}
                        useSimplePdpCoverStyles={isSimplePDPCoverFlyout}
                        variant={v}
                        onSelect={handleVariantSelect}
                        onViewDetails={handleCoverSeeDetails}
                      />
                    </Box>
                  );
                })}
              </Swiper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

CoverStep.displayName = 'CoverStep';

CoverStep.propTypes = {
  coverGroups: PropTypes.arrayOf(
    PropTypes.shape({
      itemType: PropTypes.string,
      byColor: PropTypes.array,
      byCollection: PropTypes.array,
    })
  ).isRequired,
  currency: PropTypes.string,
  isSimplePDPCoverFlyout: PropTypes.bool,
  isMobile: PropTypes.bool,
  masterMinPrice: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  selection: PropTypes.object,
};
